// @ts-check
import { insforge } from "../insforge";

/**
 * Identity & session contract. Thin pass-through over `insforge.auth.*` so
 * a future SDK swap is a one-file change. Every method resolves to a
 * `{ data, error }` shape — including when the underlying call throws — so
 * callers can rely on one branching pattern.
 *
 * Method set is driven by current callers (see issue #55). Don't add
 * methods speculatively — add when a real call site needs one.
 *
 * @typedef {{ data: any, error: any }} Result
 *
 * @typedef {Object} AuthSurface
 * @property {() => Promise<Result>} getCurrentUser
 * @property {() => Promise<Result>} getSession
 * @property {(args: { email: string, otp: string }) => Promise<Result>} verifyEmail
 * @property {(args: { email: string }) => Promise<Result>} resendVerificationEmail
 * @property {(args: { email: string, password: string, name?: string }) => Promise<Result>} signUp
 * @property {(args: { email: string, password: string }) => Promise<Result>} signInWithPassword
 * @property {(args: { provider: string, redirectTo?: string }) => Promise<Result>} signInWithOAuth
 * @property {() => Promise<Result>} signOut
 * @property {(userId?: string) => Promise<Result>} getProfile
 * @property {(patch: object) => Promise<Result>} setProfile
 */

async function callAuth(method, ...args) {
  try {
    const fn = insforge?.auth?.[method];
    if (typeof fn !== "function") {
      return {
        data: null,
        error: new Error(`backend.auth.${method}: not available on the SDK`),
      };
    }
    const result = await fn.apply(insforge.auth, args);
    if (
      result &&
      typeof result === "object" &&
      ("data" in result || "error" in result)
    ) {
      return { data: result.data ?? null, error: result.error ?? null };
    }
    return { data: result ?? null, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}

/* ── Client-side throttle (issue #68) ──────────────────────────────────────
 *
 * Tracks failed `signInWithPassword` attempts per email in localStorage.
 * After `AUTH_THROTTLE_MAX_ATTEMPTS` fails inside `AUTH_THROTTLE_WINDOW_MS`
 * the wrapper returns a synthetic `auth/rate-limited` error without hitting
 * the SDK. Successful login clears the counter. localStorage is bypassable
 * — this is a UX speed-bump for casual brute-forcers, not a security
 * boundary. Real defence is the IP-based limit on Insforge (see
 * docs/ENVIRONMENTS.md).
 */
export const AUTH_THROTTLE_KEY = "pikra_auth_throttle_v1";
export const AUTH_THROTTLE_MAX_ATTEMPTS = 5;
export const AUTH_THROTTLE_WINDOW_MS = 5 * 60 * 1000;

function _now() {
  return Date.now();
}

function _store() {
  return typeof window !== "undefined" ? window.localStorage : null;
}

function _readAll() {
  const ls = _store();
  if (!ls) return {};
  try {
    const raw = ls.getItem(AUTH_THROTTLE_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
}

function _writeAll(map) {
  const ls = _store();
  if (!ls) return;
  try {
    ls.setItem(AUTH_THROTTLE_KEY, JSON.stringify(map));
  } catch {
    // Storage disabled or full — fail open: we'd rather let users in than
    // soft-brick the login form.
  }
}

function _emailKey(email) {
  return String(email || "").trim().toLowerCase();
}

/**
 * Inspect the throttle state for an email without mutating it. Returns
 * `{ blocked, retryAfterMs, attempts }` where `retryAfterMs` is the
 * milliseconds until the window expires (only meaningful when blocked).
 *
 * @param {string} email
 */
export function getAuthThrottleState(email) {
  const key = _emailKey(email);
  if (!key) return { blocked: false, retryAfterMs: 0, attempts: 0 };
  const map = _readAll();
  const entry = map[key];
  if (!entry) return { blocked: false, retryAfterMs: 0, attempts: 0 };

  const elapsed = _now() - entry.firstFailAt;
  if (elapsed >= AUTH_THROTTLE_WINDOW_MS) {
    // Window expired — treat as a clean slate. Don't write here; the next
    // failure will overwrite the entry.
    return { blocked: false, retryAfterMs: 0, attempts: 0 };
  }
  const blocked = entry.fails >= AUTH_THROTTLE_MAX_ATTEMPTS;
  return {
    blocked,
    retryAfterMs: blocked ? AUTH_THROTTLE_WINDOW_MS - elapsed : 0,
    attempts: entry.fails,
  };
}

/**
 * Forget any throttle state for an email. Used by tests and could be
 * exposed for an "I'm sure I know my password" support flow later.
 *
 * @param {string} email
 */
export function clearAuthThrottle(email) {
  const key = _emailKey(email);
  if (!key) return;
  const map = _readAll();
  if (key in map) {
    delete map[key];
    _writeAll(map);
  }
}

function _recordFailure(email) {
  const key = _emailKey(email);
  if (!key) return;
  const map = _readAll();
  const entry = map[key];
  if (!entry || _now() - entry.firstFailAt >= AUTH_THROTTLE_WINDOW_MS) {
    map[key] = { fails: 1, firstFailAt: _now() };
  } else {
    entry.fails += 1;
    map[key] = entry;
  }
  _writeAll(map);
}

function _rateLimitedError(retryAfterMs) {
  const err = new Error("auth/rate-limited");
  // @ts-expect-error — augmenting Error with structured fields the UI consumes.
  err.code = "auth/rate-limited";
  // @ts-expect-error
  err.retryAfterMs = retryAfterMs;
  return err;
}

async function signInWithPasswordThrottled(args) {
  const state = getAuthThrottleState(args?.email);
  if (state.blocked) {
    return { data: null, error: _rateLimitedError(state.retryAfterMs) };
  }
  const result = await callAuth("signInWithPassword", args);
  if (result.error) {
    _recordFailure(args?.email);
    // If this failure pushed us over the limit, surface the rate-limited
    // error on the same response so the UI can start the countdown
    // immediately instead of waiting for one more attempt.
    const after = getAuthThrottleState(args?.email);
    if (after.blocked) {
      return { data: null, error: _rateLimitedError(after.retryAfterMs) };
    }
  } else {
    clearAuthThrottle(args?.email);
  }
  return result;
}

/* ── Profile shape normalisation ───────────────────────────────────────────
 *
 * `/api/auth/profiles/*` answers with the fields nested one level down:
 *
 *   { id: "79bb…", profile: { name, username, avatar_url } }
 *
 * Every caller reads them flat (`profile.username` in CreateEvent's publish
 * guard, `profile?.name` in Settings' form seed). Passing the raw response
 * through therefore left `username` and `name` undefined, so the app kept
 * insisting the profile was empty no matter how many times it was saved —
 * the write succeeded and the read looked blank.
 *
 * Flatten here, in the one place both the fetch and the update go through,
 * so call sites keep the shape they already expect. Tolerates an
 * already-flat payload in case the endpoint changes shape later.
 */
function flattenProfile(result) {
  const { data, error } = result;
  if (error || !data || typeof data !== "object") return result;
  if (!data.profile || typeof data.profile !== "object") return result;
  const { profile, ...rest } = data;
  return { data: { ...rest, ...profile }, error };
}

/** @type {AuthSurface} */
export const auth = {
  getCurrentUser: () => callAuth("getCurrentUser"),
  getSession: () => callAuth("getSession"),
  verifyEmail: (args) => callAuth("verifyEmail", args),
  resendVerificationEmail: (args) => callAuth("resendVerificationEmail", args),
  signUp: (args) => callAuth("signUp", args),
  signInWithPassword: (args) => signInWithPasswordThrottled(args),
  signInWithOAuth: (args) => callAuth("signInWithOAuth", args),
  signOut: () => callAuth("signOut"),
  getProfile: (userId) => callAuth("getProfile", userId).then(flattenProfile),
  setProfile: (patch) => callAuth("setProfile", patch).then(flattenProfile),
};
